import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  AccountExistsResponse,
  DeclineKycResponse,
  KycQueueView,
  KycSessionDetailResponse,
  KycSessionListResponse,
  RetryKycResponse,
} from "@/api/types";
import { useQueueMutation } from "./queue-mutation";

/** Every KYC query key starts here; queue mutations invalidate the prefix. */
export const KYC_KEY = ["kyc"] as const;

export const KYC_PAGE_SIZE = 50;

/** The queue lists poll: cheap DB reads, and a stale queue is the enemy. */
export const QUEUE_LIST_OPTIONS = {
  staleTime: 20 * 1000,
  refetchInterval: 60 * 1000,
  refetchOnWindowFocus: true,
} as const;

/** The key every page of one view shares — mutations patch it as a whole. */
export const kycSessionsKey = (view: KycQueueView) =>
  [...KYC_KEY, "sessions", view] as const;

/**
 * One view of the queue, an offset page at a time ("Load more" fetches the
 * next page and appends it). Paged rather than limit-growing because `all` is
 * every session of every unverified customer — a single capped request would
 * silently hide the tail, and the client-side filter only sees loaded rows.
 */
export const kycSessionsQuery = (view: KycQueueView) =>
  infiniteQueryOptions({
    queryKey: kycSessionsKey(view),
    queryFn: ({ pageParam }) =>
      api.get<KycSessionListResponse>(
        `/admin/kyc/sessions?view=${view}&limit=${KYC_PAGE_SIZE}&offset=${pageParam}`,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      // The next offset comes from the server's own echo, never from counting
      // loaded items: an optimistic row drop (KycPage's removeAndAdvance)
      // shrinks `items`, and a count-based offset would then re-request rows
      // we already hold — the same session twice in the list.
      const next = lastPage.offset + lastPage.limit;
      return next < lastPage.total ? next : undefined;
    },
    ...QUEUE_LIST_OPTIONS,
  });

/** On-demand: the raw provider payload for the "View raw JSON" dialog. */
export const kycSessionQuery = (sessionId: string) =>
  queryOptions({
    queryKey: [...KYC_KEY, "session", sessionId],
    queryFn: () =>
      api.get<KycSessionDetailResponse>(
        `/admin/kyc/sessions/${encodeURIComponent(sessionId)}`,
      ),
    staleTime: 5 * 60 * 1000,
  });

// ---------------------------------------------------------------------------
// Mutations — the existing /admin routes, one hook each.
// ---------------------------------------------------------------------------

export const useRetryKyc = () =>
  useQueueMutation(
    [KYC_KEY],
    (input: { sessionId: string; userId: string }) =>
      api.post<RetryKycResponse>("/admin/retry-kyc", {
        sessionId: input.sessionId,
      }),
    { quietErrors: () => true },
  );

export const useDeclineKyc = () =>
  useQueueMutation(
    [KYC_KEY],
    (input: { sessionId: string; userId: string }) =>
      api.post<DeclineKycResponse>("/admin/decline-kyc", {
        sessionId: input.sessionId,
      }),
    { quietErrors: () => true },
  );

/**
 * The decline half of "existing account" failed *after* the customer was
 * notified. Carries the underlying error so the page can still triage it
 * (stale 400/404) while knowing the email/push did go out.
 */
export class DeclineAfterNotifyError extends Error {
  constructor(readonly declineError: unknown) {
    super("The customer was notified, but declining the session failed");
    this.name = "DeclineAfterNotifyError";
  }
}

/**
 * "Existing account": notify the customer they already have an account and,
 * unless told otherwise, decline the session in the same breath (two audited
 * calls; a failure on the second leaves the row queued for a re-click).
 */
export const useNotifyAccountExists = () =>
  useQueueMutation(
    [KYC_KEY],
    async (input: {
      sessionId: string;
      userId: string;
      alsoDecline: boolean;
    }) => {
      const notified = await api.post<AccountExistsResponse>(
        "/admin/account-exists",
        { userId: input.userId },
      );
      if (!input.alsoDecline) return { notified, declined: null };
      try {
        const declined = await api.post<DeclineKycResponse>(
          "/admin/decline-kyc",
          { sessionId: input.sessionId },
        );
        return { notified, declined };
      } catch (error) {
        // Tag it: only from here on is "account-exists sent" true.
        throw new DeclineAfterNotifyError(error);
      }
    },
    { quietErrors: () => true },
  );
